import { useEffect, useState } from "react";
import { RestaurantList } from "../models/IRestaurantDetail";
import { getRestaurantList } from "../services/restaurant";
import { IFeedbackItem, IFeedbackPaging, IFeedbackResponse } from "../models/IFeedback";
import { getPendingFeedbacks, approveFeedback, deleteFeedback } from "../services/feedback";

const PAGE_SIZE = 10;

const EMPTY_PAGING: IFeedbackPaging = {
  indexFrom: 0, pageIndex: 0, pageSize: PAGE_SIZE, totalCount: 0, totalPages: 0, hasPreviousPage: false, hasNextPage: false,
};

const starColor = (val: number) => {
  if (val >= 4) return "#f59e0b";
  if (val >= 3) return "#fb923c";
  if (val >= 2) return "#f87171";
  return "#ef4444";
};

const renderStars = (point: number) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <i
        key={i}
        className={`bi ${i <= point ? "bi-star-fill" : "bi-star"}`}
        style={{ color: i <= point ? starColor(point) : "#d1d5db", fontSize: "0.85rem" }}
      />
    );
  }
  return <span className="d-inline-flex gap-0" style={{ gap: "1px" }}>{stars}</span>;
};

function FeedbackManagementPage() {
  const [restaurantList, setRestaurantList] = useState<RestaurantList>();
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>("");
  const [feedbackData, setFeedbackData] = useState<IFeedbackResponse | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [confirmModal, setConfirmModal] = useState<{ type: "approve" | "delete"; item: IFeedbackItem } | null>(null);

  useEffect(() => {
    getRestaurantList().then((res) => {
      if (res.data) setRestaurantList(res.data);
    });
  }, []);

  useEffect(() => {
    if (selectedRestaurantId) {
      fetchFeedbacks(0);
    } else {
      setFeedbackData(null);
    }
  }, [selectedRestaurantId]);

  const fetchFeedbacks = (page: number) => {
    setLoading(true);
    getPendingFeedbacks(selectedRestaurantId, page, PAGE_SIZE)
      .then((res) => {
        // Bekleyen feedback yoksa API items/paging alanlarını null döndürebiliyor
        setFeedbackData({ items: res.data?.items ?? [], paging: res.data?.paging ?? { ...EMPTY_PAGING, pageIndex: page } });
        setPageIndex(page);
      })
      .catch(() => setFeedbackData(null))
      .finally(() => setLoading(false));
  };

  const handleApprove = async (item: IFeedbackItem) => {
    setActionLoading((p) => ({ ...p, [item.id]: true }));
    setConfirmModal(null);
    try {
      await approveFeedback(item.id);
      fetchFeedbacks(pageIndex);
    } catch {
    } finally {
      setActionLoading((p) => ({ ...p, [item.id]: false }));
    }
  };

  const handleDelete = async (item: IFeedbackItem) => {
    setActionLoading((p) => ({ ...p, [item.id]: true }));
    setConfirmModal(null);
    try {
      await deleteFeedback(item.id);
      fetchFeedbacks(pageIndex);
    } catch {
    } finally {
      setActionLoading((p) => ({ ...p, [item.id]: false }));
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="container py-4" style={{ maxWidth: "1200px" }}>

      {/* Başlık */}
      <div className="d-flex align-items-center mb-4">
        <div style={{ width: "4px", height: "28px", background: "#8b5cf6", borderRadius: "3px", marginRight: "12px" }} />
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
            <i className="bi bi-chat-square-heart-fill me-2" style={{ color: "#8b5cf6" }}></i>
            Feedback Yönetimi
          </h4>
          <div className="text-muted" style={{ fontSize: "0.82rem" }}>Müşteri geri bildirimlerini onayla veya sil</div>
        </div>
      </div>

      {/* Restoran seçici */}
      <div className="mb-4" style={{ maxWidth: "440px" }}>
        <label className="form-label text-muted small fw-semibold mb-1 text-uppercase" style={{ letterSpacing: "0.05em" }}>
          <i className="bi bi-building me-1"></i>Restoran
        </label>
        <select
          className="form-select form-select-lg"
          style={{ borderRadius: "12px", border: "1.5px solid #dee2e6", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", fontWeight: 500 }}
          value={selectedRestaurantId}
          onChange={(e) => setSelectedRestaurantId(e.target.value)}
        >
          <option value="" disabled>Restoran seçiniz...</option>
          {restaurantList?.items.map((item) => (
            <option key={item.id} value={item.id}>{item.restourantName}</option>
          ))}
        </select>
      </div>

      {/* Boş seçim */}
      {!selectedRestaurantId && (
        <div className="text-center py-5">
          <i className="bi bi-hand-index-thumb" style={{ fontSize: "3rem", color: "#d1d5db" }}></i>
          <div className="mt-3 text-muted fw-semibold">Lütfen bir restoran seçin</div>
        </div>
      )}

      {/* Loading */}
      {selectedRestaurantId && loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2 text-muted small">Yükleniyor...</div>
        </div>
      )}

      {/* Feedback içerik */}
      {selectedRestaurantId && !loading && feedbackData && (
        <>
          {/* İstatistik */}
          <div className="d-flex gap-3 mb-4 flex-wrap">
            <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: "#faf5ff", border: "1px solid #e9d5ff" }}>
              <i className="bi bi-chat-square-text-fill" style={{ color: "#8b5cf6" }}></i>
              <span className="fw-semibold" style={{ color: "#6b21a8", fontSize: "0.88rem" }}>
                {feedbackData.paging.totalCount} Feedback
              </span>
            </div>
            <div className="d-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <i className="bi bi-file-earmark-text" style={{ color: "#16a34a" }}></i>
              <span className="fw-semibold" style={{ color: "#166534", fontSize: "0.88rem" }}>
                Sayfa {feedbackData.paging.pageIndex + 1} / {feedbackData.paging.totalPages || 1}
              </span>
            </div>
          </div>

          {/* Boş durum */}
          {feedbackData.items.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-emoji-smile" style={{ fontSize: "3rem", color: "#d1d5db" }}></i>
              <div className="mt-3 text-muted fw-semibold">Bekleyen feedback yok</div>
            </div>
          ) : (
            <>
              {/* Feedback kartları */}
              <div className="row g-3">
                {feedbackData.items.map((item) => {
                  const isLoading = actionLoading[item.id];
                  return (
                    <div key={item.id} className="col-12 col-md-6 col-xl-4">
                      <div
                        className="h-100 rounded-4 p-0 overflow-hidden"
                        style={{
                          border: "1.5px solid #e5e7eb",
                          background: "#fff",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                          transition: "box-shadow 0.2s, transform 0.2s",
                          opacity: isLoading ? 0.55 : 1,
                          pointerEvents: isLoading ? "none" : "auto",
                        }}
                      >
                        {/* Kart header */}
                        <div
                          className="d-flex align-items-center justify-content-between px-3 py-2"
                          style={{ background: "#faf5ff", borderBottom: "1px solid #f3e8ff" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: "34px", height: "34px", borderRadius: "50%",
                                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
                              }}
                            >
                              {item.customerName?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <div className="fw-bold" style={{ fontSize: "0.88rem", color: "#1e293b" }}>
                                {item.customerName}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                {formatDate(item.createdAt)}
                              </div>
                            </div>
                          </div>
                          <div
                            className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                            style={{
                              background: item.feedbackPointAvg >= 4 ? "#dcfce7" : item.feedbackPointAvg >= 3 ? "#fef9c3" : "#fee2e2",
                              border: `1px solid ${item.feedbackPointAvg >= 4 ? "#86efac" : item.feedbackPointAvg >= 3 ? "#fde047" : "#fca5a5"}`,
                            }}
                          >
                            <i className="bi bi-star-fill" style={{ color: starColor(item.feedbackPointAvg), fontSize: "0.78rem" }}></i>
                            <span style={{
                              fontWeight: 700, fontSize: "0.82rem",
                              color: item.feedbackPointAvg >= 4 ? "#166534" : item.feedbackPointAvg >= 3 ? "#854d0e" : "#991b1b",
                            }}>
                              {item.feedbackPointAvg % 1 === 0 ? item.feedbackPointAvg : item.feedbackPointAvg.toFixed(1)}
                            </span>
                          </div>
                        </div>

                        {/* Puanlar */}
                        <div className="px-3 pt-3 pb-2">
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center justify-content-between">
                              <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                <i className="bi bi-lightning-charge-fill me-1" style={{ color: "#3b82f6" }}></i>Hız
                              </span>
                              {renderStars(item.feedbackSpeedPoint)}
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                <i className="bi bi-hand-thumbs-up-fill me-1" style={{ color: "#10b981" }}></i>Servis
                              </span>
                              {renderStars(item.feedbackServicePoint)}
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                <i className="bi bi-egg-fried me-1" style={{ color: "#f59e0b" }}></i>Lezzet
                              </span>
                              {renderStars(item.feedbackTeastePoint)}
                            </div>
                          </div>
                        </div>

                        {/* Yorum */}
                        {item.feedbackDescription && (
                          <div className="px-3 pb-2">
                            <div
                              className="rounded-3 p-2 mt-1"
                              style={{ background: "#f8fafc", border: "1px solid #f1f5f9", fontSize: "0.84rem", color: "#374151", lineHeight: 1.5 }}
                            >
                              <i className="bi bi-quote me-1" style={{ color: "#94a3b8" }}></i>
                              {item.feedbackDescription}
                            </div>
                          </div>
                        )}

                        {/* Aksiyonlar */}
                        <div className="d-flex gap-2 px-3 pb-3 pt-1">
                          <button
                            className="btn btn-sm flex-fill"
                            onClick={() => setConfirmModal({ type: "approve", item })}
                            disabled={isLoading}
                            style={{
                              borderRadius: "10px", fontWeight: 600, fontSize: "0.82rem",
                              background: "linear-gradient(135deg, #10b981, #059669)",
                              color: "#fff", border: "none",
                              boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
                              padding: "8px 0",
                            }}
                          >
                            <i className="bi bi-check-circle-fill me-1"></i>Onayla
                          </button>
                          <button
                            className="btn btn-sm flex-fill"
                            onClick={() => setConfirmModal({ type: "delete", item })}
                            disabled={isLoading}
                            style={{
                              borderRadius: "10px", fontWeight: 600, fontSize: "0.82rem",
                              background: "#fff", color: "#dc2626",
                              border: "1.5px solid #fca5a5",
                              padding: "8px 0",
                            }}
                          >
                            <i className="bi bi-trash3-fill me-1"></i>Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {feedbackData.paging.totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap">
                  <button
                    className="btn btn-sm"
                    disabled={!feedbackData.paging.hasPreviousPage}
                    onClick={() => fetchFeedbacks(pageIndex - 1)}
                    style={{
                      borderRadius: "10px", fontWeight: 600, fontSize: "0.84rem",
                      border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151",
                      padding: "7px 16px", opacity: feedbackData.paging.hasPreviousPage ? 1 : 0.5,
                    }}
                  >
                    <i className="bi bi-chevron-left me-1"></i>Önceki
                  </button>

                  {Array.from({ length: feedbackData.paging.totalPages }, (_, i) => (
                    <button
                      key={i}
                      className="btn btn-sm"
                      onClick={() => fetchFeedbacks(i)}
                      style={{
                        borderRadius: "10px", fontWeight: 700, fontSize: "0.84rem",
                        minWidth: "38px", padding: "7px 0",
                        background: pageIndex === i ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : "#fff",
                        color: pageIndex === i ? "#fff" : "#374151",
                        border: pageIndex === i ? "none" : "1.5px solid #e5e7eb",
                        boxShadow: pageIndex === i ? "0 2px 8px rgba(139,92,246,0.3)" : "none",
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    className="btn btn-sm"
                    disabled={!feedbackData.paging.hasNextPage}
                    onClick={() => fetchFeedbacks(pageIndex + 1)}
                    style={{
                      borderRadius: "10px", fontWeight: 600, fontSize: "0.84rem",
                      border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151",
                      padding: "7px 16px", opacity: feedbackData.paging.hasNextPage ? 1 : 0.5,
                    }}
                  >
                    Sonraki<i className="bi bi-chevron-right ms-1"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Onay/Sil Modal */}
      {confirmModal && (
        <>
          <div
            onClick={() => setConfirmModal(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", zIndex: 1050 }}
          />
          <div style={{
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            background: "#fff", borderRadius: "18px", padding: "32px 28px",
            width: "100%", maxWidth: "400px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            textAlign: "center", zIndex: 1051,
          }}>
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%",
              background: confirmModal.type === "approve" ? "#f0fdf4" : "#fef2f2",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <i
                className={`bi ${confirmModal.type === "approve" ? "bi-check-circle-fill" : "bi-trash3-fill"}`}
                style={{ fontSize: "1.5rem", color: confirmModal.type === "approve" ? "#16a34a" : "#dc2626" }}
              ></i>
            </div>
            <h5 style={{ fontWeight: 800, marginBottom: "8px" }}>
              {confirmModal.type === "approve" ? "Feedback'i Onayla" : "Feedback'i Sil"}
            </h5>
            <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: "8px" }}>
              <strong style={{ color: "#111827" }}>{confirmModal.item.customerName}</strong> adlı müşterinin feedback'ini
              {confirmModal.type === "approve" ? " onaylamak" : " silmek"} istediğinize emin misiniz?
            </p>
            {confirmModal.type === "delete" && (
              <p style={{ color: "#dc2626", fontSize: "0.8rem", marginBottom: "24px" }}>Bu işlem geri alınamaz.</p>
            )}
            {confirmModal.type === "approve" && <div style={{ marginBottom: "24px" }} />}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setConfirmModal(null)}
                style={{
                  flex: 1, padding: "11px", borderRadius: "10px",
                  border: "1.5px solid #e5e7eb", background: "#fff",
                  fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", color: "#374151",
                }}
              >
                İptal
              </button>
              <button
                onClick={() =>
                  confirmModal.type === "approve"
                    ? handleApprove(confirmModal.item)
                    : handleDelete(confirmModal.item)
                }
                style={{
                  flex: 1, padding: "11px", borderRadius: "10px",
                  border: "none",
                  background: confirmModal.type === "approve" ? "#16a34a" : "#dc2626",
                  fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", color: "#fff",
                }}
              >
                {confirmModal.type === "approve" ? (
                  <><i className="bi bi-check-circle-fill me-1"></i>Evet, Onayla</>
                ) : (
                  <><i className="bi bi-trash3-fill me-1"></i>Evet, Sil</>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default FeedbackManagementPage;
